import React from 'react';
import {
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

interface AlertProps {
  variant?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  children: React.ReactNode;
  className?: string;
  onClose?: () => void;
}

const variantConfig = {
  info: {
    container: 'bg-blue-50 border-blue-200',
    icon: InformationCircleIcon,
    iconColor: 'text-blue-400',
    title: 'text-blue-800',
    content: 'text-blue-700',
  },
  success: {
    container: 'bg-green-50 border-green-200',
    icon: CheckCircleIcon,
    iconColor: 'text-green-400',
    title: 'text-green-800',
    content: 'text-green-700',
  },
  warning: {
    container: 'bg-yellow-50 border-yellow-200',
    icon: ExclamationTriangleIcon,
    iconColor: 'text-yellow-400',
    title: 'text-yellow-800',
    content: 'text-yellow-700',
  },
  error: {
    container: 'bg-red-50 border-red-200',
    icon: XCircleIcon,
    iconColor: 'text-red-400',
    title: 'text-red-800',
    content: 'text-red-700',
  },
};

export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  title,
  children,
  className = '',
  onClose,
}) => {
  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <div className={`rounded-md border p-4 ${config.container} ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon className={`h-5 w-5 ${config.iconColor}`} />
        </div>
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={`text-sm font-medium ${config.title}`}>
              {title}
            </h3>
          )}
          <div className={`${title ? 'mt-2' : ''} text-sm ${config.content}`}>
            {children}
          </div>
        </div>
        {onClose && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onClose}
                className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${config.iconColor} hover:${config.container}`}
              >
                <span className="sr-only">Dismiss</span>
                <XCircleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};